/**
 * Video Captions / SRT Generator — standalone gap tool.
 * POST /api/gap/captions/generate → SRT file content + cue table preview.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Subtitles, Loader2, Copy, Download, ArrowLeft, Sparkles } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import "../workflows/workflows.css";

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function SRTGeneratorPage() {
  const navigate = useNavigate();
  const [text, setText] = useState("");
  const [cueDuration, setCueDuration] = useState(4);
  const [charsPerCue, setCharsPerCue] = useState(80);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (loading || text.length < 20) {
      toast.error("Paste at least 20 characters of transcript text");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const res = await axios.post(`${API}/api/gap/captions/generate`, {
        text, cue_duration_seconds: Number(cueDuration), chars_per_cue: Number(charsPerCue),
      });
      setResult(res.data);
      toast.success(`${res.data.cue_count} cues generated`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Generation failed");
    } finally {
      setLoading(false);
    }
  };

  const copySrt = () => {
    if (!result) return;
    navigator.clipboard.writeText(result.srt);
    toast.success("SRT copied");
  };

  const downloadSrt = () => {
    if (!result) return;
    const blob = new Blob([result.srt], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "captions.srt"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <Header />
      <div className="wf-hub" data-testid="srt-generator-page">
        <div className="wf-hub-inner" style={{ maxWidth: 880 }}>
          <button className="wf-btn wf-btn--ghost" onClick={() => navigate("/tools")} data-testid="srt-back" style={{ marginBottom: 24 }}>
            <ArrowLeft size={14}/> All tools
          </button>
          <span className="wf-eyebrow">Video captions / SRT generator</span>
          <h1 className="wf-hub-title" data-testid="srt-title">Transcript in. <em>SRT out.</em></h1>
          <p className="wf-hub-sub" style={{ marginBottom: 36 }}>
            Paste your transcript or any text. We&rsquo;ll smart-chunk it into properly timed SRT cues — ready to upload to YouTube,
            Premiere, or any player. Used inside the <button onClick={() => navigate("/workflows/education")} style={{ background: "none", border: "none", color: "#6d28d9", textDecoration: "underline", cursor: "pointer", padding: 0 }}>Education workflow</button>.
          </p>

          <form onSubmit={submit} data-testid="srt-form">
            <div style={{ marginBottom: 18 }}>
              <label style={{ display: "block", fontWeight: 600, marginBottom: 6, fontSize: "0.95rem" }}>Transcript text *</label>
              <textarea value={text} onChange={(e) => setText(e.target.value)} rows={8} required
                placeholder="Paste your full transcript or script here…"
                data-testid="srt-text"
                style={{ width: "100%", padding: "12px 14px", borderRadius: 10, border: "1px solid rgba(109,40,217,0.18)", fontSize: "0.92rem", background: "white", fontFamily: "inherit", lineHeight: 1.55 }}/>
              <div style={{ fontSize: "0.8rem", color: "#5a4d6f", marginTop: 4 }}>{text.length} characters</div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 14, marginBottom: 18 }}>
              <div>
                <label style={{ display: "block", fontWeight: 600, marginBottom: 6, fontSize: "0.9rem" }}>Cue duration (seconds)</label>
                <input type="number" value={cueDuration} min={2} max={10} onChange={(e) => setCueDuration(e.target.value)} data-testid="srt-duration"
                  style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: "1px solid rgba(109,40,217,0.18)", background: "white" }}/>
              </div>
              <div>
                <label style={{ display: "block", fontWeight: 600, marginBottom: 6, fontSize: "0.9rem" }}>Max chars per cue</label>
                <input type="number" value={charsPerCue} min={30} max={200} onChange={(e) => setCharsPerCue(e.target.value)} data-testid="srt-chars"
                  style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: "1px solid rgba(109,40,217,0.18)", background: "white" }}/>
              </div>
            </div>
            <button type="submit" className="wf-btn" disabled={loading || text.length < 20} data-testid="srt-submit">
              {loading ? <><Loader2 size={16} className="animate-spin"/> Generating…</> : <><Sparkles size={16}/> Generate SRT</>}
            </button>
          </form>

          {result && (
            <div style={{ marginTop: 36 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
                <h2 style={{ fontSize: "1.3rem", fontWeight: 700, margin: 0 }} data-testid="srt-result-title">
                  <Subtitles size={20} style={{ display: "inline", marginRight: 8, verticalAlign: "-3px", color: "#6d28d9" }}/>
                  {result.cue_count} cues • {result.total_duration_seconds}s
                </h2>
                <div style={{ display: "flex", gap: 8 }}>
                  <button className="wf-btn wf-btn--ghost" onClick={copySrt} data-testid="srt-copy"><Copy size={14}/> Copy</button>
                  <button className="wf-btn" onClick={downloadSrt} data-testid="srt-download"><Download size={14}/> Download .srt</button>
                </div>
              </div>
              <div className="wf-asset" style={{ marginTop: 0 }}>
                <pre style={{ whiteSpace: "pre-wrap", fontSize: "0.85rem", fontFamily: "ui-monospace, monospace", margin: 0, color: "#1a1422", maxHeight: 480, overflowY: "auto" }} data-testid="srt-output">
                  {result.srt}
                </pre>
              </div>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
}
